import React, { useState, useEffect } from 'react';
import { LOGIN_URL, PROFILE_URL, areas } from '../config';
import styled from 'styled-components';
import Alert from 'react-bootstrap/Alert';
import { MenuItem, Select, FormControl, Chip, Tooltip } from '@mui/material';
import { useLocation } from './AreaContext';
import WorldMap from './WorldMap';

const LoginPage = styled.div`
  display: flex;
  height: 100vh;
  overflow: auto;
  background-color: #111;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const ContentSection = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  background: linear-gradient(to right, #011328, #0e2c4f);
  color: white;
  z-index: 1;
  min-height: 100vh;
  overflow: hidden;
  user-select: none;

  @media (max-width: 800px) {
    width: 100%;
    flex: none;
    order: 1;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-top: 20px;
  margin-bottom: 20px;
  color: #dfebeb;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  text-align: center;
`;

const DottedMap = styled.img`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 800px;
  opacity: 0.3;
  pointer-events: none;
  z-index: -1;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: black;
  color: white;
`;

const LoadingText = styled.p`
  font-size: 1.0rem;
  margin-bottom: 20px;
`;

const LoadingGif = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 10px;
`;

const MapSection = styled.div`
  position: relative;
  flex: 1;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  user-select: none;
  background: transparent;
  padding: 0;

  @media (max-width: 800px) {
    width: 100%;
    height: 500px;
    min-height: 500px;
    flex: none;
    order: 2;
  }
`;

const Login = ({ clusterIsActiveMap = {} }) => {
  const [loginError, setLoginError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoginHovered, setIsLoginHovered] = useState(false); // Added hover state
  const { selectedLocation, setSelectedLocation } = useLocation();

  useEffect(() => {
    let isMounted = true;
    const checkAuthentication = async () => {
      try {
        const response = await fetch(PROFILE_URL, {
          credentials: 'include',
        });

        if (!isMounted) return;

        if (response.ok) {
          setLoginError('');
          window.location.href = '/';
        } else {
          setLoading(false);
          setLoginError('');
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        if (isMounted) {
          setLoading(false);
          setLoginError('Login Failed. If problems persist, please contact your system administrator.');
        }
      }
    };

    checkAuthentication();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = () => {
    if (selectedLocation) {
      window.location.href = LOGIN_URL;
    } else {
      setLoginError('Please select area first.');
    }
  };

  if (loading) {
    return (
        <LoadingContainer>
          <LoadingText>
            Loading
            <LoadingGif src="images/ajax-loader.gif" alt="Loading" />
          </LoadingText>
        </LoadingContainer>
    );
  }

  return (
      <LoginPage>
        <MapSection>
          <WorldMap
              areas={areas}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              clusterIsActiveMap={clusterIsActiveMap}
          />
        </MapSection>

        <ContentSection>
          <DottedMap className="login-map-2" src="images/map2.png" />

          <Title>
            <img src="images/vlara-logo.svg" alt="V-LARA" style={{ width: '44px' }} />
            LARA Web Booking Client
          </Title>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', marginBottom: '24px' }}>
            <FormControl style={{ width: '100%' }}>
              <Select
                  className="text-fields no-border-radius"
                  id="area-select"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    backgroundColor: '#fafafa',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
              >
                {areas
                    .slice()
                    .sort((a, b) => {
                      if (a.value === 'efin') return -1;
                      if (b.value === 'efin') return 1;

                      const getWeight = (item) => {
                        if (item.chip) {
                          switch (item.chip.className) {
                            case 'notoperational':
                              return 1;
                            case 'underdevelopment':
                            case 'testenvironment':
                              return 3;
                            default:
                              return 5;
                          }
                        } else if (clusterIsActiveMap[item.value]) {
                          return 1;
                        } else {
                          return 3;
                        }
                      };

                      return getWeight(a) - getWeight(b);
                    })
                    .map(({ value, label, chip, home }) => (
                        <MenuItem
                            key={value}
                            value={value}
                            style={{
                              display: 'flex',
                              alignItem: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 14px'
                            }}
                        >
                <span
                    style={{
                      flex: 1,
                      fontWeight: clusterIsActiveMap[value] ? '600' : '400',
                      color: clusterIsActiveMap[value] ? '#41826e' : 'inherit',
                    }}
                    className={home ? 'login-finland' : ''}
                >
                  {label}
                </span>

                          {chip ? (
                              <Chip label={chip.label} size="small" className={`status-chip ${chip.className}`} style={{ marginLeft: 12, height: '22px' }} />
                          ) : clusterIsActiveMap[value] ? (
                              <Chip label="Operational" size="small" className="status-chip operational" style={{ marginLeft: 12, height: '22px' }} />
                          ) : (
                              <Chip label="Not operational" size="small" className="status-chip notoperational" style={{ marginLeft: 12, height: '22px' }} />
                          )}
                        </MenuItem>
                    ))}
              </Select>
            </FormControl>

            <button
                onClick={handleLogin}
                onMouseEnter={() => setIsLoginHovered(true)}
                onMouseLeave={() => setIsLoginHovered(false)}
                style={{
                  width: '100%',
                  border: '0',
                  padding: '8px',
                  color: 'white',
                  backgroundColor: isLoginHovered ? '#004085' : '#0056b3',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
            >
              Login with VATSIM
            </button>
          </div>

          {loginError && <p style={{ color: 'red' }}>{loginError}</p>}

          <Alert key={'success'} variant={'success'} style={{ width: '100%', borderRadius: '0', lineHeight: '1rem' }}>
            <b>Local and sub-regional airspace management support system</b>
            <br/>
            <p style={{ paddingTop: '8px', marginBottom: '0', fontSize: '0.9rem' }}>Create and manage airspace reservations on VATSIM network.</p>
          </Alert>

          <div className="flag-container">
            <a href="https://vatsim-scandinavia.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/fi.png" alt="Finland" title="Finland" />
            </a>
            <a href="https://beluxvacc.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/be.png" alt="Belgium" title="Belgium" />
            </a>
            <a href="https://vatsim-scandinavia.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/dk.png" alt="Denmark" title="Denmark" />
            </a>
            <a href="https://www.estvacc.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/ee.png" alt="Estonia" title="Estonia" />
            </a>
            <a href="https://vatsim.fr/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/fr.png" alt="France" title="France" />
            </a>
            <a href="https://vatsim-germany.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/de.png" alt="Germany" title="Germany" />
            </a>
            <a href="https://vateir.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/ie.png" alt="Ireland" title="Ireland" />
            </a>
            <a href="https://main.vatil.org.il/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/il.png" alt="Israel" title="Israel" />
            </a>
            <a href="https://www.vatsim.uk/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/gb.png" alt="United Kingdom" title="United Kingdom" />
            </a>
            <a href="https://vatsim-scandinavia.org/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/no.png" alt="Norway" title="Norway" />
            </a>
            <a href="https://vatphil.com/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/ph.png" alt="Philippines" title="Philippines" />
            </a>
            <a href="https://www.vatsim-arabian.com/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/ae.png" alt="United Arab Emirates" title="United Arab Emirates" />
            </a>
            <a href="https://kuwait-iraqvacc.net/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/iq.png" alt="Iraq" title="Iraq" />
            </a>
            <a href="https://kuwait-iraqvacc.net/" target="_blank" rel="noopener noreferrer">
              <img src="https://flagcdn.com/h40/kw.png" alt="Kuwait" title="Kuwait" />
            </a>
            <a href="https://virtualnato.org/home/" target="_blank" rel="noopener noreferrer">
              <img src="images/vNato.png" alt="vNATO" title="vNATO" />
            </a>
            <a href="https://virtualfiaf.org/" target="_blank" rel="noopener noreferrer">
              <img src="images/vFiAF.png" alt="vFiAF/vFBG" title="vFiAF/vFBG" />
            </a>
          </div>
        </ContentSection>
      </LoginPage>
  );
};

export default Login;